import axios from "axios";
import {
  DeviceType,
  REQUEST_STATUS,
  TENANT_KEY,
} from "../../../utils/constants";
import { notification } from "antd";
import { ROUTES } from "../../core/navigation";
import dayjs from "dayjs";

const HOST_NAME = process.env.REACT_APP_API_HOST_NAME + "/api/";

export const detectDeviceType = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth < 600) {
    return DeviceType.mobile;
  } else if (screenWidth < 992) {
    return DeviceType.tablet;
  } else {
    return DeviceType.pc;
  }
};

export const capitalizeWord = (word) => {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export function getMobileOperatingSystem() {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone";
  }

  if (/android/i.test(userAgent)) {
    return "Android";
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "iOS";
  }

  return "unknown";
}

export function getIsDesktopBrowser() {
  return window.orientation === undefined;
}

export const constructUrlWithParamsForFetch = (params) => {
  let esc = encodeURIComponent;
  let query = Object.keys(params)
    .map((k) => esc(k) + "=" + esc(params[k]))
    .join("&");
  return query;
};

const createURL = (path) => HOST_NAME + path;

export const sendRequest = async (
  method,
  url,
  params = null,
  data = null,
  isFormData = false,
) => {
  url = params
    ? createURL(url) + "?" + constructUrlWithParamsForFetch(params)
    : createURL(url);

  const contentType = isFormData ? false : "application/json";
  const processData = isFormData ? false : true;

  let dataPromise = {};

  let request = axios({
    method: method,
    url: url,
    data: data,
    contentType: contentType,
    processData: processData,
    headers: {
      "X-TenantId": process.env.REACT_APP_API_TENANT_ID,
    },
  });

  return await request
    .then((response) => {
      dataPromise = response?.data;
      if (dataPromise.status === REQUEST_STATUS.error) {
        notification.error({
          message: "An Unexpected Error Occured",
          description: dataPromise.message,
        });
      }

      return dataPromise.data;
    })
    .catch((error) => {
      notification.error({
        message: "An Unexpected Error Occured",
        description: error.message,
      });
      throw error;
    });
};

export const calculateAge = (dob) => {
  const age = dayjs().diff(dayjs(dob), "year");

  return `${age} ${age === 1 ? "year" : "years"}`;
};

export const timeAgo = (date) => {
  if (!date) {
    return "";
  }

  const now = new Date();
  const secondsPast = Math.floor((now - new Date(date)) / 1000);

  if (secondsPast < 60) {
    return `${secondsPast} seconds ago`;
  } else if (secondsPast < 3600) {
    // less than an hour
    const minutes = Math.floor(secondsPast / 60);
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  } else if (secondsPast < 86400) {
    // less than a day
    const hours = Math.floor(secondsPast / 3600);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  } else if (secondsPast < 2592000) {
    // less than 30 days
    const days = Math.floor(secondsPast / 86400);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  } else if (secondsPast < 31536000) {
    // less than a year
    const months = Math.floor(secondsPast / 2592000);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  } else {
    const years = Math.floor(secondsPast / 31536000);
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  }
};

export function formatCurrency(
  amount,
  currency = "LKR",
  locale = "en-US",
  minimumFractionDigits = 2,
) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "";
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits,
  }).format(amount);
}

export function isValidMobile(number) {
  if (!number) return false;

  const cleaned = number.replace(/[\s-]/g, "");

  // Regex for:
  // 07XXXXXXXX
  // +947XXXXXXXX
  // 947XXXXXXXX
  const sriLankaMobileRegex = /^(?:\+94|94|0)?7\d{8}$/;

  return sriLankaMobileRegex.test(cleaned);
}

export const isLoadingRoute = () => {
  const path = window.location.pathname;
  return path === "/" || path.includes(ROUTES.initializing);
};

export const getTenant = () => localStorage.getItem(TENANT_KEY);
export const setTenant = (val) => localStorage.setItem(TENANT_KEY, val);
export const removeTenant = () => localStorage.removeItem(TENANT_KEY);

export const pluralizeText = (value, text) => {
  if (value > 1) {
    return text + "s";
  }
  return text;
};

export const downloadFile = async (url, fileName = "download") => {
  try {
    const response = await fetch(url, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error("Failed to download file");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download error:", error);
  }
};

export const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
  } else {
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  }
};
